import React, { useEffect, useState } from "react";
import { Paper, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, Box, Radio, FormControlLabel, Divider } from "@mui/material";
import agent from "../../app/api/agent";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../app/stores/store";

function formatDate(dateStr?: string) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
}

export default function DuplicateMembersPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modalStore = useStore((s) => s.modalStore);
    const [groups, setGroups] = useState<any[][]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedKeep, setSelectedKeep] = useState<{ [groupIdx: number]: string }>({});

    useEffect(() => {
        setLoading(true);
        agent.Members.getDuplicates().then((data) => {
            setGroups(data);
            setLoading(false);
        });
    }, []);

    const handleSubmitGroup = (groupIdx: number) => {
        const group = groups[groupIdx];
        const keepId = selectedKeep[groupIdx];
        const keepMember = group.find((m: any) => m.id === keepId);
        if (!keepId || !keepMember) return;

        modalStore.openModal(
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {t("members.confirmKeepMember", { memberNumber: keepMember.memberNumber })}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {t("members.confirmArchiveOthers")}
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={async () => {
                            await agent.Members.archiveDuplicates({
                                keepMemberId: keepId,
                                archiveMemberIds: group.filter((m: any) => m.id !== keepId).map((m: any) => m.id)
                            });
                            setGroups(groups => groups.filter((_, idx) => idx !== groupIdx));
                            modalStore.closeModal();
                        }}
                    >
                        {t("general.confirm")}
                    </Button>
                    <Button variant="outlined" color="error" onClick={modalStore.closeModal}>
                        {t("general.cancel")}
                    </Button>
                </Box>
            </Box>
        );
    };

    // Show number of duplicate groups
    const totalDuplicateGroups = groups.length;

    return (
        <Paper sx={{ p: 3, maxWidth: 900, mx: "auto", mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                {t("members.duplicateMembersTitle")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
                {t("members.duplicateMembersInfo")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
                {t("members.totalDuplicates")} - {totalDuplicateGroups}
            </Typography>
            {loading ? (
                <Typography>{t("general.loading")}</Typography>
            ) : groups.length === 0 ? (
                <Typography>{t("members.noDuplicatesFound") || "No duplicates found."}</Typography>
            ) : (
                groups.map((group, idx) => (
                    <React.Fragment key={idx}>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {group[0].firstName} {group[0].lastName}
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell>{t("members.memberNumber")}</TableCell>
                                            <TableCell>{t("members.firstName")}</TableCell>
                                            <TableCell>{t("members.lastName")}</TableCell>
                                            <TableCell>{t("members.dateOfBirth")}</TableCell>
                                            <TableCell>{t("members.email")}</TableCell>
                                            <TableCell>{t("members.actions") || "Actions"}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {group.map((member: any) => (
                                            <TableRow
                                                key={member.id}
                                                hover
                                                sx={{ cursor: "pointer" }}
                                            >
                                                <TableCell>
                                                    <FormControlLabel
                                                        value={member.id}
                                                        control={
                                                            <Radio
                                                                checked={selectedKeep[idx] === member.id}
                                                                onChange={() => setSelectedKeep({ ...selectedKeep, [idx]: member.id })}
                                                            />
                                                        }
                                                        label=""
                                                        sx={{ m: 0 }}
                                                    />
                                                </TableCell>
                                                <TableCell>{member.memberNumber}</TableCell>
                                                <TableCell>{member.firstName}</TableCell>
                                                <TableCell>{member.lastName}</TableCell>
                                                <TableCell>{formatDate(member.dateOfBirth)}</TableCell>
                                                <TableCell>{member.email}</TableCell>
                                                <TableCell
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => navigate(`/update-member/${member.id}`)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        {t("members.editData")}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box sx={{ mt: 2, textAlign: "right" }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    disabled={!selectedKeep[idx]}
                                    onClick={() => handleSubmitGroup(idx)}
                                >
                                    {t("members.submitKeepAndArchive")}
                                </Button>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                    </React.Fragment>
                ))
            )}
        </Paper>
    );
}
